"use client";
import React, { createContext, useState, useContext, useCallback } from "react";

const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  // Products list cache: { [cacheKey]: { products: [], lastDoc: null, hasMore: true, totalCount: 0 } }
  const [productsCache, setProductsCache] = useState({});

  // Categories cache: { categories: [], counts: {} }
  const [categoriesCache, setCategoriesCache] = useState(null);

  // Product details cache: { [productId]: productData }
  const [productDetailsCache, setProductDetailsCache] = useState({});

  // ============ PRODUCTS LIST CACHE ============
  const getCacheKey = (category) => category || "all";

  const getCachedData = useCallback((category) => {
    const key = getCacheKey(category);
    return productsCache[key] || null;
  }, [productsCache]);

  const setCachedData = useCallback((category, data) => {
    const key = getCacheKey(category);
    setProductsCache(prev => ({
      ...prev,
      [key]: data
    }));
  }, []);

  const appendToCache = useCallback((category, newProducts, lastDoc, hasMore) => {
    const key = getCacheKey(category);
    setProductsCache(prev => {
      const existing = prev[key] || { products: [], totalCount: 0 };
      return {
        ...prev,
        [key]: {
          ...existing,
          products: [...existing.products, ...newProducts],
          lastDoc,
          hasMore
        }
      };
    });
  }, []);

  const clearProductsCache = useCallback((category = null) => {
    if (category) {
      const key = getCacheKey(category);
      setProductsCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else {
      setProductsCache({});
    }
  }, []);

  // ============ CATEGORIES CACHE ============
  const getCategoriesCache = useCallback(() => {
    return categoriesCache;
  }, [categoriesCache]);

  const setCategoriesCacheData = useCallback((categories, counts) => {
    setCategoriesCache({ categories, counts });
  }, []);

  const clearCategoriesCache = useCallback(() => {
    setCategoriesCache(null);
  }, []);

  // ============ PRODUCT DETAILS CACHE ============
  const getProductDetailsCache = useCallback((productId) => {
    return productDetailsCache[productId] || null;
  }, [productDetailsCache]);

  const setProductDetailsData = useCallback((productId, productData) => {
    setProductDetailsCache(prev => ({
      ...prev,
      [productId]: productData
    }));
  }, []);

  const clearProductDetailsCache = useCallback((productId = null) => {
    if (productId) {
      setProductDetailsCache(prev => {
        const newCache = { ...prev };
        delete newCache[productId];
        return newCache;
      });
    } else {
      setProductDetailsCache({});
    }
  }, []);

  // ============ CLEAR ALL CACHE ============
  const clearAllCache = useCallback(() => {
    setProductsCache({});
    setCategoriesCache(null);
    setProductDetailsCache({});
  }, []);

  return (
    <ProductContext.Provider value={{
      // Products list
      getCachedData,
      setCachedData,
      appendToCache,
      clearProductsCache,
      // Categories
      getCategoriesCache,
      setCategoriesCacheData,
      clearCategoriesCache,
      // Product details
      getProductDetailsCache,
      setProductDetailsData,
      clearProductDetailsCache,
      // All
      clearAllCache
    }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => useContext(ProductContext);
